import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import PageLayout from '../pagelayout';
import {
  Table,
  TableCellTh,
  TableCellTd,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import { useLoader } from '../api/useLoader';
import {
  MinimalAktivitetInfoDto as MinimalAktivitetUHDto,
  MinimalAktivitetInfoDtoAktivitetVurderingStatusDtoEnum as StatusDtoEnumUH,
} from '../api/generated/studiested-generated-api';
import {
  MinimalAktivitetInfoDto as MinimalAktivitetPSDto,
  MinimalAktivitetInfoDtoAktivitetVurderingStatusDtoEnum as StatusDtoEnumPS,
} from '../api/generated/praksissted-generated-api';
import { useIntl } from 'react-intl';
import Spinner from '../components/spinner/spinner';
import Button from '../components/button/button';
import translations from './evaluationactivities.i18n';
import useTableSearch from '../components/table/useTableSearch';
import { dateWithLocale } from '../date.utils';
import { Language } from '../i18n/LanguageStore';
import * as css from './evaluationactivities.module.scss';
import { NavLink, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { EmptyState } from '../components/emptyState/emptyState';
import { UserType } from '../util/userType';
import EvaluationTabsWrapper from './evaluationtabs';
import { associateBy } from '../util';

enum TableHeaders {
  Periode = 'Periode',
  Praksisaktivitet = 'Praksisaktivitet',
  Praksisklasse = 'Praksisklasse',
  Studenter = 'Studenter',
  Status = 'Status',
  Handling = 'Handling',
}

type TableHeaderToString = {
  [key in TableHeaders]: string;
};

type TableHeaderForSearch = Pick<
  TableHeaderToString,
  TableHeaders.Praksisaktivitet | TableHeaders.Praksisklasse
>;

const TableHeadersSort = [
  TableHeaders.Periode,
  TableHeaders.Praksisaktivitet,
  TableHeaders.Praksisklasse,
];

const EvaluationActivitiesWrapper: React.FC<
  React.PropsWithChildren<{ userType: UserType }>
> = ({ userType }) => {
  const { vurderingApi: UHVurderingApi } = useContext(StudiestedApiContext);
  const { vurderingApi: PSVurderingApi } = useContext(PraksisstedApiContext);
  const { data, loading } = useLoader(() =>
    (userType === UserType.UH
      ? UHVurderingApi
      : PSVurderingApi
    ).vurderingAktiviteterGet(),
  );

  const { formatMessage } = useIntl();

  const labels = useMemo(
    () => ({
      title: formatMessage({
        id: translations.title.id,
      }),
      subtitle: formatMessage({
        id: translations.subtitle.id,
      }),
    }),
    [formatMessage],
  );

  return (
    <Routes>
      <Route path="/*" element={<Outlet />}>
        <Route
          path=":activityId/*"
          element={<EvaluationTabsWrapper userType={userType} />}
        />
        <Route
          path="*"
          element={
            <PageLayout
              title={labels.title}
              subtitle={labels.subtitle}
              isIndented
            >
              <EvaluationActivitiesContainer
                aktiviteter={data?.aktiviteter}
                loading={loading}
              />
            </PageLayout>
          }
        />
      </Route>
    </Routes>
  );
};

const EvaluationActivitiesContainer: React.FC<
  React.PropsWithChildren<{
    aktiviteter: MinimalAktivitetUHDto[] | MinimalAktivitetPSDto[] | undefined;
    loading: boolean;
  }>
> = ({ aktiviteter, loading }) => {
  useEffect(() => {
    sessionStorage.setItem(
      'evaluationActivities',
      JSON.stringify(
        associateBy(
          aktiviteter || [],
          (aktivitet: MinimalAktivitetUHDto | MinimalAktivitetPSDto) =>
            aktivitet.aktivitetId,
        ),
      ),
    );
  }, [aktiviteter]);
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      emptyStateTitle: formatMessage({
        id: translations.emptyStateTitle.id,
      }),
      emptyStateText: formatMessage({
        id: translations.emptyStateText.id,
      }),
    }),
    [formatMessage],
  );

  if (loading) {
    return <Spinner height={'3em'} />;
  }

  if (!aktiviteter || aktiviteter.length === 0)
    return (
      <EmptyState title={labels.emptyStateTitle} text={labels.emptyStateText} />
    );

  return <ActivitiesTable aktiviteter={aktiviteter} />;
};

const ActivitiesTable: React.FC<
  React.PropsWithChildren<{
    aktiviteter: MinimalAktivitetUHDto[] | MinimalAktivitetPSDto[];
  }>
> = ({ aktiviteter }) => {
  const [searchWords, setSearchWords] = useState<TableHeaderForSearch>({
    [TableHeaders.Praksisaktivitet]: '',
    [TableHeaders.Praksisklasse]: '',
  });

  const navigate = useNavigate();

  const { formatMessage, locale } = useIntl();

  const translateStatusToLabel = useCallback(
    (status: StatusDtoEnumUH | StatusDtoEnumPS) => {
      switch (status) {
        case StatusDtoEnumUH.Framtidig:
        case StatusDtoEnumPS.Framtidig:
          return formatMessage({ id: translations.statusFramtidig.id });
        case StatusDtoEnumUH.Aktiv:
        case StatusDtoEnumPS.Aktiv:
          return formatMessage({ id: translations.statusAktiv.id });
        case StatusDtoEnumUH.Gjennomfoert:
        case StatusDtoEnumPS.Gjennomfoert:
          return formatMessage({ id: translations.statusGjennomfoert.id });
        default:
          return '';
      }
    },
    [formatMessage],
  );

  const filteredAktiviteter = useTableSearch(
    aktiviteter,
    [
      [
        searchWords[TableHeaders.Praksisaktivitet],
        (aktivitet: MinimalAktivitetUHDto | MinimalAktivitetPSDto) =>
          `${aktivitet.emnekode} - ${aktivitet.praksistypenavn}`,
      ],
      [
        searchWords[TableHeaders.Praksisklasse],
        (aktivitet: MinimalAktivitetUHDto | MinimalAktivitetPSDto) =>
          aktivitet.praksisklassenavn,
      ],
    ],
    1,
  );

  const labels = useMemo(
    () => ({
      periode: formatMessage({ id: translations.periode.id }),
      praksisaktivitet: formatMessage({
        id: translations.praksisaktivitet.id,
      }),
      praksisklasse: formatMessage({
        id: translations.praksisklasse.id,
      }),
      studenter: formatMessage({ id: translations.studenter.id }),
      status: formatMessage({ id: translations.status.id }),
      handling: formatMessage({ id: translations.handling.id }),
      handlingButton: formatMessage({ id: translations.handlingButton.id }),
    }),
    [formatMessage],
  );

  const headersWithTranslations: TableHeaderToString = useMemo(
    () => ({
      [TableHeaders.Periode]: labels.periode,
      [TableHeaders.Praksisaktivitet]: labels.praksisaktivitet,
      [TableHeaders.Praksisklasse]: labels.praksisklasse,
      [TableHeaders.Studenter]: labels.studenter,
      [TableHeaders.Status]: labels.status,
      [TableHeaders.Handling]: labels.handling,
    }),
    [labels],
  );

  const initActiveCol = useMemo(
    () => Object.keys(headersWithTranslations).indexOf(TableHeaders.Periode),
    [headersWithTranslations],
  );

  const [activeCol, setActiveCol] = useState(initActiveCol);

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = useCallback(
    (
      col: number,
      a: MinimalAktivitetUHDto | MinimalAktivitetPSDto,
      b: MinimalAktivitetUHDto | MinimalAktivitetPSDto,
    ) => {
      switch (Object.keys(TableHeaders)[col]) {
        case TableHeaders.Periode:
          return compare(
            `${Date.parse(a.startDato).toString()}${Date.parse(
              a.sluttDato,
            ).toString()}`,
            `${Date.parse(b.startDato).toString()}${Date.parse(
              b.sluttDato,
            ).toString()}`,
          );
        case TableHeaders.Praksisaktivitet:
          return compare(
            `${a.emnekode} - ${a.praksistypenavn}${a.praksisklassenavn}`,
            `${b.emnekode} - ${b.praksistypenavn}${b.praksisklassenavn}`,
          );
        case TableHeaders.Praksisklasse:
          return compare(a.praksisklassenavn, b.praksisklassenavn);
      }
      return 0;
    },
    [compare],
  );

  const sortAktiviteter = useCallback(
    (
      col: number,
      ascending: boolean,
      aktiviteterToSort: MinimalAktivitetUHDto[] | MinimalAktivitetPSDto[],
    ) =>
      [...aktiviteterToSort].sort((a, b) =>
        ascending ? sort(col, b, a) : sort(col, a, b),
      ),
    [sort],
  );

  const [sortState, setSortState] = useState<{
    col: number;
    ascending: boolean;
  }>({ col: initActiveCol, ascending: false });

  const headers = useMemo(
    () =>
      Object.entries(headersWithTranslations).map(([type, title], index) => (
        <TableCellTh
          key={`${type}-${title}`}
          colIndex={index}
          activeCol={index === activeCol}
          setActiveCol={setActiveCol}
          sort={(col, ascending) => setSortState({ col, ascending })}
          canSort={TableHeadersSort.includes(type as TableHeaders)}
          search={(col, value) =>
            setSearchWords({ ...searchWords, [type]: value })
          }
          canSearch={Object.keys(searchWords).includes(type)}
        >
          {title}
        </TableCellTh>
      )),
    [headersWithTranslations, activeCol, setSearchWords, searchWords],
  );

  const getRows = useCallback(
    (aktiviteter: MinimalAktivitetUHDto[] | MinimalAktivitetPSDto[]) =>
      aktiviteter.map((aktivitet) => {
        const activityDate = `${dateWithLocale(
          new Date(aktivitet.startDato),
          locale as Language,
        )} - ${dateWithLocale(
          new Date(aktivitet.sluttDato),
          locale as Language,
        )}`;
        return (
          <TableRow key={aktivitet.aktivitetId}>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Periode]}
              className={css.periodCell}
            >
              {activityDate}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Praksisaktivitet]}
              className={css.practiceactivityCell}
            >
              <NavLink to={`/calendar/activity/${aktivitet.aktivitetId}`}>
                {aktivitet.emnekode} - {aktivitet.praksistypenavn}
              </NavLink>
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Praksisklasse]}
              className={css.classCell}
            >
              {aktivitet.praksisklassenavn}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Studenter]}
              className={css.studentCell}
            >
              {aktivitet.studenter}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Status]}
              className={css.statusCell}
            >
              {translateStatusToLabel(aktivitet.aktivitetVurderingStatus)}
            </TableCellTd>
            <TableCellTd
              header={headersWithTranslations[TableHeaders.Handling]}
              className={css.buttonCell}
            >
              <Button.Secondary
                role={'link'}
                onClick={() => navigate(aktivitet.aktivitetId)}
              >
                {labels.handlingButton}
              </Button.Secondary>
            </TableCellTd>
          </TableRow>
        );
      }),
    [locale, labels, translateStatusToLabel, headersWithTranslations, navigate],
  );

  const rows = useMemo(
    () =>
      getRows(
        sortAktiviteter(
          sortState.col,
          sortState.ascending,
          filteredAktiviteter,
        ),
      ),
    [filteredAktiviteter, sortState, sortAktiviteter, getRows],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>{headers}</TableRow>
      </TableHeader>
      <TableMain>{rows}</TableMain>
    </Table>
  );
};

export default EvaluationActivitiesWrapper;
